import type { ClientPaginatedResponse } from "api-types/common/commonResponses";
import type { HomePlanContractSchemaUpdateOne } from "api-types/homePlanContracts/homePlanContractRequests";
import type { ClientHomePlanContractHydrated } from "api-types/homePlanContracts/homePlanContractResponses";
import type { FetchError } from "common/types/fetchError";
import { getHomePlanContractsURL, getHomePlanContractURL } from "lib/endpointHelpers";
import { queryOptions, skipToken, useMutation, useQuery, useQueryClient, type QueryClient } from "lib/query";
import { authDelete, authGet, authPut } from "lib/query/hooks/useAuthQuery";
import { customerKeys, homePlanContractKeys, homePlanKeys } from "lib/query/keys";

// Sorting happens in queryFn, not a `select`: `select` is a fresh closure on every render (this
// factory is called anew each time), so React Query would re-sort on every unrelated re-render of
// the surrounding table (pagination, column sort, focus) instead of only on an actual fetch.
export const homePlanContractsQueryOptions = (builderId: string, homePlanId: string) =>
  queryOptions({
    queryKey: homePlanContractKeys.byHomePlan(builderId, homePlanId),
    queryFn: async () => {
      const response = await authGet<ClientPaginatedResponse<ClientHomePlanContractHydrated>>(
        getHomePlanContractsURL(builderId, homePlanId),
      );
      return { ...response, data: [...response.data].sort((a, b) => b.createdAt.localeCompare(a.createdAt)) };
    },
  });

export function useHomePlanContracts({
  builderId,
  homePlanId,
  enabled = true,
}: {
  builderId: string;
  homePlanId: string;
  enabled?: boolean;
}) {
  const options = homePlanContractsQueryOptions(builderId, homePlanId);
  const query = useQuery({ ...options, queryFn: enabled ? options.queryFn : skipToken });
  return { ...query, contracts: query.data?.data ?? [], loading: query.isLoading, queryKey: options.queryKey };
}

function invalidateContractQueries(queryClient: QueryClient, builderId: string): void {
  void queryClient.invalidateQueries({ queryKey: homePlanContractKeys.all(builderId) });
  void queryClient.invalidateQueries({ queryKey: homePlanKeys.all(builderId) });
  void queryClient.invalidateQueries({ queryKey: customerKeys.all(builderId) });
}

interface HomePlanContractUpdate {
  contractId: string;
  fields: HomePlanContractSchemaUpdateOne;
}

export function useUpdateHomePlanContract(builderId: string) {
  const queryClient = useQueryClient();
  return useMutation<ClientHomePlanContractHydrated, FetchError, HomePlanContractUpdate>({
    mutationKey: [...homePlanContractKeys.all(builderId), "update"],
    mutationFn: ({ contractId, fields }) =>
      authPut<HomePlanContractSchemaUpdateOne, ClientHomePlanContractHydrated>(
        getHomePlanContractURL(builderId, contractId),
        fields,
      ),
    onSuccess: () => invalidateContractQueries(queryClient, builderId),
  });
}

export function useDeleteHomePlanContract(builderId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, FetchError, string>({
    mutationKey: [...homePlanContractKeys.all(builderId), "delete"],
    mutationFn: (contractId) => authDelete(getHomePlanContractURL(builderId, contractId)),
    onSuccess: () => invalidateContractQueries(queryClient, builderId),
  });
}
